import { Spiner } from "../../../components/spinner/Spiner";
import { useEffect, useRef, useState } from "react";
import { auth } from "../../../firebase";
import Button from "../../../components/button/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { EventParticipationWebSearch } from "../eventParticipationWeb/EventParticipationWebSearch";
import companiesQuery from "../../../firestore/Companies.query";
import { convertDateToJapaneseStringWithWeekZero, convertTimeToString } from "../../../utility/ConvertUtily";
import { ConstMessage } from "../../../config/const";
import { EventParticipationAttendanceHistorySortParam, EventParticipationHistory, EventParticipationTimestampToDate } from "../../../model/EventParticipationHistory";
import messagesQuery from "../../../firestore/Messages.query";
import { alertOpen, AlertValue } from "../../../features/alert.slice";
import { useDispatch } from "react-redux";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";

// MEMO: アクセスした端末によってボタンの表示を制御する処理を入れる手間を省くため別ソースとしている
// MEMO: レイアウト以外は EventParticipationWeb とほぼ同じ処理
export const EventParticipationMobile = () => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // ログイン中のユーザー情報
  const currentUser = auth.currentUser;
  const [wholeScreenLoad, setWholeScreenLoad] = useState<boolean>(true);
  const [isSearchingLoad, setIsSearchingLoad] = useState<boolean>(true);

  const [allHistories, setAllHistories] = useState<EventParticipationHistory[]>([]);
  const [eventParticipationHistoryList, setEventParticipationHistoryList] = useState<EventParticipationHistory[]>([]);
  const [rowList, setRowList] = useState<any[]>([]);
  const [displayCount, setDisplayCount] = useState<number>(50);

  const [historySortParam, setHistorySortParam] = useState<EventParticipationAttendanceHistorySortParam>({
    title: null,
    eventAt: "desc"
  });

  const listLimit = useRef<number>(50);
  useEffect(() => {
    const windowBreakPoint = 1024;
    if (window.innerWidth >= windowBreakPoint) {
      // 画面幅が1024px以上の場合
      navigate("/portal/eventParticipationWeb");
    }
  }, [location.pathname, navigate]);

  // 初回取得
  useEffect(() => {
    (async () => {
      try {
        // 団体を取得
        const company = await companiesQuery.getCompany(currentUser?.tenantId!);
        if (currentUser?.displayName && currentUser?.uid) {
          // イベント参加履歴全データ取得（旧イベント参加履歴データ含む）
          const result = await messagesQuery.getEventParticipationAttendanceHistory(
            company.id,
            currentUser.displayName,
            currentUser.uid
          );
          const converted = result.map(EventParticipationTimestampToDate);
          setAllHistories(converted);
        }
      } catch (err) {
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_GET_DATA, "danger")));
      } finally {
        setWholeScreenLoad(false);
      }
    })();
  }, []);

  // ソートまたは表示件数変更時に反映
  useEffect(() => {
    const sorted = [...allHistories].sort((a, b) => {
      const aDate = new Date(a.eventAt).getTime();
      const bDate = new Date(b.eventAt).getTime();

      if (historySortParam.eventAt) {
        return historySortParam.eventAt === "asc" ? aDate - bDate : bDate - aDate;
      }
      if (historySortParam.title) {
        return historySortParam.title === "asc"
          ? a.title.localeCompare(b.title)
          : b.title.localeCompare(a.title);
      }
      return 0;
    });

    const sliced = sorted.slice(0, displayCount);
    setEventParticipationHistoryList(sliced);
    setIsSearchingLoad(false);
  }, [historySortParam, allHistories, displayCount]);

  // 表示用データ更新
  useEffect(() => {
    rowListDataSet(eventParticipationHistoryList);
  }, [eventParticipationHistoryList]);

  // 履歴を追加取得
  const onClickMoreHistory = async () => {
    setIsSearchingLoad(true);
    setDisplayCount(prev => prev + listLimit.current);
  };

  // 表示用データ
  async function rowListDataSet(historyData: EventParticipationHistory[]) {
    const rowData: any[] = await Promise.all(
      historyData.map(async (history) => ({
        title: history.title,
        eventAt: history.eventAt,
        attendanceButtonName: history.attendanceButtonName,
        menu: (
          <Button
            onClick={() => navigate(`/portal/eventParticipationMobile/${history.messageId}/info`)}
            className={"h-10 text-sm items-center whitespace-pre w-[55px] h-[50px]"}
            color={"darkOrange"}
            children="読む" />
        )
      }))
    );
    setRowList(rowData);
  }

  return (
    <div className="h-full w-full">
      {wholeScreenLoad ? (
        <Spiner />
      ) : (
        <>
          <nav className="flex items-start px-4 pt-3" aria-label="Breadcrumb">
            <button
              className="hidden flex px-4 justify-center font-medium rounded-md shadow transition ease-in duration-200 rounded h-10 px-2 text-sm items-center text-orange-500 bg-white border-2 border-orange-500 hover:bg-orange-500 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 focus:ring-offset-orange-100"
              onClick={() => {}}
            >
              <ChevronLeftIcon className="-ml-2 h-5 w-5" aria-hidden="true" />
              <span>戻る</span>
            </button>
          </nav>
          <div className="flex items-center justify-center py-3 px-1 mx-2 border-b">
            <h2 className="text-2xl font-semibold text-gray-900">
              イベント参加履歴
            </h2>
          </div>
          <div className={'py-2 px-1 mx-2 border-b'}>
            <EventParticipationWebSearch
              stateHistorySortParam={historySortParam}
              stateSetHistorySortParam={setHistorySortParam}
            />
          </div>
          <div className={'px-2 pb-6'}>
            {rowList.map((item) => (
              <>
                <div className="flex flex-col border-b py-2">
                  <div className="flex justify-between items-center">
                    <span className="w-fit pr-4">
                      <div className="flex justify-center items-center whitespace-pre relative text-orange-500 border-orange-500 border-[3px] border-solid border-r-0 font-bold h-8 w-fit px-1 sm:px-2">
                        <span className="absolute border-orange-500 border-t-[3px] border-r-[3px] right-[-0.7rem] h-[1.4rem] w-[1.4rem] transform rotate-45" />
                        {item.attendanceButtonName}
                      </div>
                    </span>
                    <div className="flex flex-wrap justify-end">
                      <span className="text-sm leading-tight sm:text-base">{convertDateToJapaneseStringWithWeekZero(new Date(item.eventAt))}</span>
                      <span className="text-sm leading-tight sm:text-base">{convertTimeToString(new Date(item.eventAt))}</span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <p className="w-10/12 text-xl font-semibold">{item.title}</p>
                    {item.menu}
                  </div>
                </div>
              </>
            ))}
            <div className={"my-3 flex justify-center"}>
              {isSearchingLoad ?
                <div className={"py-1"}><Spiner /></div>
                :
                (allHistories.length > rowList.length) ?
                  <Button
                    onClick={() => onClickMoreHistory()}
                    className="h-12 w-2/3 items-center font-bold"
                    color="darkOrange"
                    children="もっと見る" />
                  :
                  <></>
              }
            </div>
          </div>
        </>
      )}
    </div>
  );
};
